import { faBed, faList, faLocationDot, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FC, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCities, getDatesByCity, getTypes } from 'services/package';
import { SelectGrouped } from 'ts/package/interfaces/Package';
import { SearchPackageModel } from 'ts/package/classes/SearchPackageModel';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { Select } from 'ts/interfaces/Generic';
import Button from 'components/ui/Button/Button';
import { SearchDate } from 'ts/package/interfaces/Package';
import { getDisabledDates } from 'utils/package';
import { addDays, formatPriceCurrency } from 'utils/utils';
import { onClickClose, onClickOverlay } from 'utils/responsive';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { faCalendar } from '@fortawesome/free-regular-svg-icons';
import useRecaptcha from 'hooks/useRecaptcha';
import { dateFormat } from 'utils/shared/utils';
import * as idb from 'utils/shared/idbUrl';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { flightSourcesDef } from 'constants/package';
import { MainContext } from 'contexts/MainContext';
import { b2b, isNetravel } from 'constants/generic';

interface SearchPackagesProps {
  searchBox?: string;
  cityCode?: string;
}

const SearchPackages: FC<SearchPackagesProps> = ({ searchBox, cityCode }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [types, setTypes] = useState<Select[]>([]);
  const [roomTypes, setRoomTypes] = useState<Select[]>([]);
  const [cities, setCities] = useState<SelectGrouped[]>();
  const [searchModel, setSearchModel] = useState(new SearchPackageModel());
  const [departureDates, setDepartureDates] = useState<SearchDate[]>();
  const [urlParams] = useSearchParams();
  const { isRecaptchaReady, handleRecapcha } = useRecaptcha();
  const { userSiteConfig } = useContext(MainContext);
  const isResultPage = location.pathname.includes('results');

  useEffect(() => {
    const b2bRoutes = ['/ilpackages', '/ilpackages/results', '/'];
    if (b2b && !b2bRoutes.includes(location.pathname.toLowerCase())) {
      return;
    }
    if (!isRecaptchaReady) return;

    init();
  }, [isRecaptchaReady, location.pathname, i18n.language]); // eslint-disable-line

  const init = async () => {
    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;
    const searchTypes = await getTypes();

    setTypes(searchTypes.types);
    setRoomTypes(searchTypes.roomTypes);

    if (searchTypes.types?.length) {
      if (urlParams.get('type')) {
        const objParams = Object.fromEntries(urlParams.entries());
        const cityCode = objParams.cityCode.split('-')[0];
        setSearchModel(new SearchPackageModel(urlParams));
        const newCities = await getCities(objParams.type);
        setCities(newCities || []);

        const newDepartureDates = await getDatesByCity({
          recaptchaToken,
          cityCode,
          type: parseInt(objParams.type),
          flightSourcesDefault: objParams.flightSourcesDefault || 'ALP',
        });
        setDepartureDates(newDepartureDates || []);
      } else {
        let type = searchTypes.types[0].value as string;

        if (searchBox === 'LDS_SPA') {
          type = '9';
        } else if (searchBox === 'LDS_SKI') {
          type = '8';
        }

        setSearchModel({
          ...searchModel,
          type,
          roomType: searchTypes.roomTypes[1].value as string,
        });
        onChangeType(type);
      }
    }
  };

  const onChangeType = async (value: string) => {
    setSearchModel((prevSearchModel) => ({
      ...prevSearchModel,
      type: value,
      city: '',
      cityCode: '',
      checkIn: undefined,
      checkOut: undefined,
    }));
    setDepartureDates(undefined);
    setCities(undefined);

    const newCities = await getCities(value);

    setCities(newCities || []);
    if (newCities?.length) {
      const firstCityCode = (
        (newCities[0].options.length && newCities[0].options[0]) ||
        ((newCities[1].options.length && newCities[1].options[0]) as Select)
      ).value as string;

      let cityCodeVal = firstCityCode;

      if (cityCode) {
        const findOption = (option: Select) =>
          option.value?.toString().toLowerCase().includes(cityCode?.toLowerCase());

        const matchingGroup = newCities.find((group) => group.options.some(findOption));
        const matchingOption = matchingGroup?.options.find(findOption);

        if (matchingOption) {
          cityCodeVal = matchingOption.value as string;
        }
      }

      onChangeCity(cityCodeVal, value, newCities);
    }
  };

  const onChangeCity = async (value: string, type: string, newCities: SelectGrouped[]) => {
    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;
    setSearchModel((prevSearchModel) => {
      const group = newCities.find((item) => item.options.some((opt) => opt.value === value));
      const city = group?.options.find((opt) => opt.value === value)?.label;

      return {
        ...prevSearchModel,
        city: city || '',
        cityCode: value,
        checkIn: undefined,
        checkOut: undefined,
      };
    });
    setDepartureDates(undefined);

    const newDepartureDates = await getDatesByCity({
      recaptchaToken,
      cityCode: value?.split('-')[0],
      type: parseInt(type),
      flightSourcesDefault: searchModel.flightSourcesDefault,
    });
    setDepartureDates(newDepartureDates || []);
    if (newDepartureDates?.length) {
      onChangeDepartureDate(newDepartureDates[0].date);
    }
  };

  const onChangeDepartureDate = async (value: Date) => {
    setSearchModel((prevSearchModel) => {
      return { ...prevSearchModel, checkIn: value, checkOut: undefined };
    });
  };

  const onClickSearch = () => {
    idb.deletePageStateByPartialKey('ilpackages');
    if (searchModel.checkIn) {
      const searchParams = new URLSearchParams({
        type: searchModel.type,
        cityCode: searchModel.cityCode,
        checkIn: dateFormat(searchModel.checkIn, 'yyyy-MM-dd'),
        roomType: searchModel.roomType,
        city: searchModel.city,
        flightSourcesDefault: searchModel.flightSourcesDefault || '',
      }).toString();

      const element = document.querySelector('.search-wrapper') as HTMLElement;
      element?.classList?.remove('visible-responsive');

      const path = `/ILPackages/results`;
      if (location.search.slice(1) !== searchParams) {
        navigate({ pathname: path, search: searchParams });
      } else {
        window.location.reload();
      }

      if (isNetravel) {
        const cityGroup = cities?.find((city) =>
          city.options.some((opt) => opt.value === searchModel.cityCode)
        );
        const city = cityGroup?.options.find((opt) => opt.value === searchModel.cityCode)?.label;

        const roomType = roomTypes.find((room) => room.value === searchModel.roomType)?.label;
        sessionStorage.setItem('netravelSearchParams', JSON.stringify({ city, roomType }));

        const searchJsx = document.querySelector('.search-wrapper');

        if (searchJsx) {
          searchJsx.classList.remove('visible-responsive');
        }
      }
    }
  };

  const onChangeFlightSources = async (value: string) => {
    setDepartureDates(undefined);

    setSearchModel({
      ...searchModel,
      flightSourcesDefault: value,
      checkIn: undefined,
    });

    const recaptchaToken = await handleRecapcha();
    if (!recaptchaToken) return;

    const newDepartureDates = await getDatesByCity({
      recaptchaToken,
      cityCode: searchModel.cityCode?.split('-')[0],
      type: parseInt(searchModel.type),
      flightSourcesDefault: value,
    });
    setDepartureDates(newDepartureDates || []);
    if (newDepartureDates?.length) {
      onChangeDepartureDate(newDepartureDates[0].date);
    }
  };

  const onClickNewSearch = () => {
    navigate('/ilpackages');
    idb.deletePageStateByPartialKey('ilpackages');

    setTimeout(() => {
      const latestPageUrl = localStorage.getItem('latestPageUrl');
      if (latestPageUrl) {
        const latestPageUrlObj = JSON.parse(latestPageUrl);
        localStorage.setItem(
          'latestPageUrl',
          JSON.stringify({ ...latestPageUrlObj, ilpackage: [] })
        );
      }
    }, 300);
  };

  return (
    <div className="search-wrapper package-search-wrapper" onClick={onClickOverlay}>
      <div className="search-inner">
        <div className="results-responsive-header">
          <span className="title text-md">{t('search.search')}</span>
          <FontAwesomeIcon icon={faTimes} onClick={onClickClose} />
        </div>
        {isResultPage && (b2b || userSiteConfig.base.rememberModeEnabled) && (
          <div className="search-element radius">
            <Button
              title={t('home.new_search')}
              onClick={onClickNewSearch}
              className="btn btn-default"
            />
          </div>
        )}
        <div className="search-element ">
          <SelectCustom
            options={types}
            value={searchModel.type}
            onChange={onChangeType}
            movablePlaceholder={t('package.type')}
            movablePlaceholderIcon={faList}
            isLoading={!types.length}
            menuPlacement={'bottom'}
          />
        </div>
        <div className="search-element location">
          <SelectCustom
            options={cities || []}
            value={searchModel.cityCode}
            needToTranslate={true}
            onChange={(value) => onChangeCity(value, searchModel.type, cities || [])}
            movablePlaceholder={t('search.destination')}
            movablePlaceholderIcon={faLocationDot}
            isLoading={!cities}
            menuPlacement={'bottom'}
          />
        </div>
        <div className="search-element date">
          <FormGroupDatepicker
            onChange={(value) => onChangeDepartureDate(value)}
            value={searchModel.checkIn}
            label={t('flight.departing')}
            minDate={departureDates?.length ? departureDates[0]?.date : addDays(1)}
            maxDate={
              departureDates?.length ? departureDates[departureDates?.length - 1]?.date : new Date()
            }
            disabledDates={getDisabledDates(departureDates)}
            longFormat={true}
            dayContentRenderer={(date) => {
              const depDate = departureDates?.find(
                (item) => item.date.getTime() === date.getTime()
              );
              return (
                !!depDate && (
                  <span className="rdrDayCustom">
                    <span className="date">{depDate.date.getDate()}</span>
                    <span className="price">{formatPriceCurrency(depDate.minPrice, '$')}</span>
                  </span>
                )
              );
            }}
            movablePlaceholder={t('flight.departing')}
            movablePlaceholderIcon={faCalendar}
            isLoading={!departureDates}
            onClickReset={() =>
              setSearchModel({ ...searchModel, checkIn: departureDates?.[0]?.date })
            }
          />
        </div>
        <div className="search-element rooms">
          <SelectCustom
            options={roomTypes}
            value={searchModel.roomType}
            onChange={(value) => setSearchModel({ ...searchModel, roomType: value })}
            movablePlaceholder={t('package.room_type')}
            movablePlaceholderIcon={faBed}
            isLoading={!roomTypes.length}
            menuPlacement={'bottom'}
          />
        </div>

        {b2b && (
          <div className="search-element star">
            <SelectCustom
              options={flightSourcesDef}
              value={searchModel.flightSourcesDefault}
              onChange={onChangeFlightSources}
              highInput={true}
            />
          </div>
        )}

        <div className="search-button">
          <button className="btn btn-primary btn-go" onClick={onClickSearch}>
            GO
          </button>
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(SearchPackages, 'SearchPackages');
